// ConfiguracaoView - preferencias + identidade visual da organizacao

import { signal } from "@preact/signals";
import { useEffect, useState } from "preact/hooks";
import { Checkbox } from "@app/ui/components/checkbox";
import {
  AlertCircle,
  Loader2,
  Palette,
  Save,
  Settings
} from "lucide-react";
import { api, ApiError } from "../api";
import { openAlert } from "../ConfirmPromptModal";
import { friendlyError } from "@app/ui/lib/friendly-error";

const data = signal<{ name: string; settings: Record<string, unknown> } | null>(null);
const phase = signal<"loading" | "ready" | "error">("loading");
const err = signal<string | null>(null);

interface Branding {
  logoUrl?: string;
  colorPrimary?: string;
  colorSecondary?: string;
  fontFamily?: string;
  buttonRadius?: string;
  allowDarkLight?: boolean;
}

const FONT_OPTIONS = [
  { value: "", label: "Padrao (Inter)" },
  { value: "Inter", label: "Inter" },
  { value: "Roboto", label: "Roboto" },
  { value: "Open Sans", label: "Open Sans" },
  { value: "Poppins", label: "Poppins" },
  { value: "Lato", label: "Lato" },
  { value: "Nunito", label: "Nunito" },
  { value: "Montserrat", label: "Montserrat" }
];

const RADIUS_OPTIONS = [
  { value: "0", label: "Reto (0)" },
  { value: "0.25rem", label: "Levemente arredondado" },
  { value: "0.5rem", label: "Padrao" },
  { value: "0.75rem", label: "Mais arredondado" },
  { value: "1rem", label: "Bem arredondado" },
  { value: "9999px", label: "Pilula" }
];

async function load() {
  phase.value = "loading";
  err.value = null;
  try {
    const r = await api.getSettings();
    data.value = { name: r.company.name, settings: r.company.settings };
    phase.value = "ready";
  } catch (e) {
    if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
      window.location.href = "/auth/";
      return;
    }
    err.value = friendlyError(e);
    phase.value = "error";
  }
}

export function ConfiguracaoView() {
  const [busy, setBusy] = useState(false);
  const [showAvg, setShowAvg] = useState(false);
  const [studentsSeeStudents, setStudentsSeeStudents] = useState(false);
  const [gradingDefault, setGradingDefault] = useState(true);
  const [branding, setBranding] = useState<Branding>({});

  useEffect(() => {
    load().then(() => {
      if (!data.value) return;
      const s = data.value.settings;
      setShowAvg(!!s.showClassAverages);
      setStudentsSeeStudents(!!s.studentsSeeOtherStudents);
      setGradingDefault(s.gradingEnabledByDefault !== false);
      setBranding(((s.branding as Branding) || {}) as Branding);
    });
  }, []);

  async function save() {
    setBusy(true);
    try {
      const current = data.value?.settings || {};
      const cleanBranding: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(branding)) {
        if (v !== "" && v !== undefined && v !== null) cleanBranding[k] = v;
      }

      await api.saveSettings({
        ...current,
        showClassAverages: showAvg,
        studentsSeeOtherStudents: studentsSeeStudents,
        gradingEnabledByDefault: gradingDefault,
        branding: cleanBranding
      });

      void openAlert({ title: "Aviso", message: "Configuracao salva." });
      await load();
    } catch (e) {
      void openAlert({ title: "Erro", message: friendlyError(e) });
    } finally {
      setBusy(false);
    }
  }

  if (phase.value === "loading") {
    return (
      <p class="flex items-center gap-2 text-muted-foreground">
        <Loader2 className="size-4 animate-spin" /> Carregando...
      </p>
    );
  }

  if (phase.value === "error") {
    return (
      <div class="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
        <AlertCircle className="mr-2 inline size-4 text-destructive" /> {err.value}
      </div>
    );
  }

  return (
    <div class="space-y-4">
      <header>
        <h1 class="flex items-center gap-2 text-2xl font-bold tracking-tight">
          <Settings className="size-6 text-primary" />
          Configuracao
        </h1>
        <p class="mt-1 text-sm text-muted-foreground">
          Preferencias da organizacao e identidade visual.
        </p>
      </header>

      <section class="space-y-3 rounded-md border border-border bg-card/40 p-4">
        <h2 class="flex items-center gap-2 text-sm font-bold">
          <Settings className="size-4 text-primary" /> Privacidade e exibicao
        </h2>

        <label class="flex items-start gap-2 rounded-md border border-border bg-background/50 p-3 text-sm">
          <Checkbox
            checked={showAvg}
            onCheckedChange={(checked) => setShowAvg(checked === true)}
            className="mt-0.5"
          />
          <div class="min-w-0 flex-1">
            <div class="font-semibold">Mostrar medias da sala aos alunos</div>
            <div class="text-[11px] text-muted-foreground">
              Aluno ve a media da turma em cada prova ou disciplina.
            </div>
          </div>
        </label>

        <label class="flex items-start gap-2 rounded-md border border-border bg-background/50 p-3 text-sm">
          <Checkbox
            checked={studentsSeeStudents}
            onCheckedChange={(checked) => setStudentsSeeStudents(checked === true)}
            className="mt-0.5"
          />
          <div class="min-w-0 flex-1">
            <div class="font-semibold">Alunos podem ver outros alunos</div>
            <div class="text-[11px] text-muted-foreground">
              Lista de colegas da turma visivel para cada aluno.
            </div>
          </div>
        </label>

        <label class="flex items-start gap-2 rounded-md border border-border bg-background/50 p-3 text-sm">
          <Checkbox
            checked={gradingDefault}
            onCheckedChange={(checked) => setGradingDefault(checked === true)}
            className="mt-0.5"
          />
          <div class="min-w-0 flex-1">
            <div class="font-semibold">Nova disciplina ja vem com nota habilitada</div>
            <div class="text-[11px] text-muted-foreground">
              Valor padrao ao criar disciplina nova. Pode ser ajustado depois.
            </div>
          </div>
        </label>
      </section>

      <section class="space-y-3 rounded-md border border-border bg-card/40 p-4">
        <div>
          <h2 class="flex items-center gap-2 text-sm font-bold">
            <Palette className="size-4 text-primary" /> Identidade visual
          </h2>
          <p class="mt-0.5 text-[11px] text-muted-foreground">
            Aplicada nos links publicos de pagamento, recibos e e-mails enviados
            aos alunos. Sem nada configurado, usamos o visual padrao.
          </p>
        </div>

        <div class="grid gap-3 md:grid-cols-2">
          <Field label="URL do logo">
            <input
              type="url"
              value={branding.logoUrl ?? ""}
              onInput={(e) =>
                setBranding({
                  ...branding,
                  logoUrl: (e.target as HTMLInputElement).value
                })
              }
              placeholder="https://exemplo.com/logo.png"
              aria-label="URL do logo"
              class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
            />
          </Field>

          <Field label="Fonte">
            <select
              value={branding.fontFamily ?? ""}
              onChange={(e) =>
                setBranding({
                  ...branding,
                  fontFamily: (e.target as HTMLSelectElement).value
                })
              }
              aria-label="Fonte"
              class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
            >
              {FONT_OPTIONS.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Cor primaria">
            <ColorInput
              value={branding.colorPrimary ?? ""}
              onChange={(v) => setBranding({ ...branding, colorPrimary: v })}
            />
          </Field>

          <Field label="Cor secundaria">
            <ColorInput
              value={branding.colorSecondary ?? ""}
              onChange={(v) => setBranding({ ...branding, colorSecondary: v })}
            />
          </Field>

          <Field label="Arredondamento de botoes">
            <select
              value={branding.buttonRadius ?? ""}
              onChange={(e) =>
                setBranding({
                  ...branding,
                  buttonRadius: (e.target as HTMLSelectElement).value
                })
              }
              aria-label="Arredondamento"
              class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
            >
              <option value="">Padrao</option>
              {RADIUS_OPTIONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Tema claro/escuro nos links">
            <label class="flex items-center gap-2 rounded-md border border-border bg-background/50 px-3 py-1.5 text-xs">
              <Checkbox
                checked={branding.allowDarkLight !== false}
                onCheckedChange={(checked) =>
                  setBranding({
                    ...branding,
                    allowDarkLight: checked === true
                  })
                }
              />
              Permitir que o visitante escolha
            </label>
          </Field>
        </div>

        <BrandingPreview branding={branding} organizationName={data.value?.name ?? ""} />
      </section>

      <div class="flex justify-end">
        <button
          type="button"
          onClick={save}
          disabled={busy}
          class="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-xs font-bold text-white hover:brightness-110 disabled:opacity-50"
        >
          {busy ? (
            <Loader2 className="size-3.5 animate-spin" />
          ) : (
            <Save width="1em" height="1em" />
          )}
          Salvar configuracao
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: any }) {
  return (
    <label class="block">
      <span class="mb-1 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      {children}
    </label>
  );
}

function ColorInput({
  value,
  onChange
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div class="flex items-center gap-2 rounded-md border border-border bg-background px-2 py-1.5">
      <input
        type="color"
        value={value || "#8b5cf6"}
        onInput={(e) => onChange((e.target as HTMLInputElement).value)}
        aria-label="Seletor de cor"
        class="h-7 w-10 cursor-pointer rounded border border-border bg-transparent"
      />
      <input
        type="text"
        value={value}
        onInput={(e) => onChange((e.target as HTMLInputElement).value)}
        placeholder="#8b5cf6"
        aria-label="Hex"
        class="flex-1 bg-transparent font-mono text-xs outline-none"
      />
    </div>
  );
}

function BrandingPreview({
  branding,
  organizationName
}: {
  branding: Branding;
  organizationName: string;
}) {
  const primary = branding.colorPrimary || "#8b5cf6";
  const radius = branding.buttonRadius || "0.5rem";
  const font = branding.fontFamily ? `${branding.fontFamily}, system-ui` : "system-ui";

  return (
    <div class="rounded-md border border-dashed border-border bg-background/40 p-4">
      <p class="mb-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        Pre-visualizacao
      </p>
      <div
        class="flex items-center gap-3 rounded-md border border-border bg-card p-3"
        style={{ fontFamily: font }}
      >
        {branding.logoUrl ? (
          <img src={branding.logoUrl} alt="logo" class="h-8 w-auto" />
        ) : (
          <div
            class="grid size-8 place-items-center text-white"
            style={{ background: primary, borderRadius: radius }}
          >
            *
          </div>
        )}
        <span class="text-sm font-bold">{organizationName}</span>
        <button
          type="button"
          class="ml-auto px-3 py-1.5 text-xs font-bold text-white"
          style={{ background: primary, borderRadius: radius }}
        >
          Pagar agora
        </button>
      </div>
    </div>
  );
}
